/**
 * Emitted when a new intervention is created.
 *
 * **When:** After successful intervention creation in the database
 *
 * **Business Logic:** This event includes all essential data to avoid
 * additional database queries in listeners. Site and equipment IDs may be null
 * for temporary sites or interventions without equipment.
 */
export class InterventionCreatedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.status = params.status;
        this.priority = params.priority;
        this.siteId = params.siteId;
        this.equipmentId = params.equipmentId;
    }
}
/**
 * Why a status change happened, for the cases the transition itself does not say.
 *
 * One value today: `job_report`. `InterventionReportsService.signReport` turns a
 * single technician gesture into up to three events, so a consumer that owes the
 * outside world one message per gesture needs to tell those events apart.
 */
export const INTERVENTION_STATUS_CHANGE_CAUSE = {
    JOB_REPORT: 'job_report',
};
/**
 * Emitted when an intervention's status changes.
 *
 * **When:** After successful status update in the database
 *
 * **Business Logic:** Status changes are critical for workflow management.
 * Transitions like PENDING → IN_PROGRESS or IN_PROGRESS → COMPLETED may
 * trigger automated actions (notifications, SLA tracking, billing).
 */
export class InterventionStatusChangedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.oldStatus = params.oldStatus;
        this.newStatus = params.newStatus;
        this.cancellationReason = params.cancellationReason;
        this.quoteRequestType = params.quoteRequestType;
        this.quoteFeedback = params.quoteFeedback;
        this.cause = params.cause;
        this.automated = params.automated ?? false;
        this.scheduledAt = params.scheduledAt;
    }
}
/**
 * Emitted when an intervention's priority level changes.
 *
 * **When:** After successful priority update in the database
 *
 * **Business Logic:** Priority changes affect scheduling and resource allocation.
 * Escalations to HIGH or CRITICAL may trigger immediate notifications to managers.
 */
export class InterventionPriorityChangedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.oldPriority = params.oldPriority;
        this.newPriority = params.newPriority;
    }
}
/**
 * Emitted when a user is assigned to an intervention.
 *
 * **When:** After successful user assignment in the database
 *
 * **Business Logic:** User assignments are critical for task distribution.
 * The assigned user should receive immediate notification with intervention details.
 */
export class InterventionUserAssignedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.assignedUserId = params.assignedUserId;
    }
}
/**
 * Emitted when a user is unassigned from an intervention.
 *
 * **When:** After successful user removal from intervention
 *
 * **Business Logic:** Unassignments may occur due to reassignment, user unavailability,
 * or intervention cancellation. The unassigned user should be notified.
 */
export class InterventionUserUnassignedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.unassignedUserId = params.unassignedUserId;
    }
}
/**
 * Emitted when a document is uploaded to an intervention.
 *
 * **When:** After successful document upload and database record creation
 *
 * **Business Logic:** Document uploads are important for compliance and audit trails.
 * Certain document types (e.g., CERTIFICATE, COMPLIANCE) may trigger workflow steps.
 * File size is included for storage metrics and quota management.
 *
 * Supports both user and technician actors via mutually exclusive fields.
 */
export class InterventionDocumentUploadedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.documentId = params.documentId;
        this.documentType = params.documentType;
        this.originalFilename = params.originalFilename;
        this.fileSizeBytes = params.fileSizeBytes;
        this.mimeType = params.mimeType;
    }
}
/**
 * Emitted when a document is deleted from an intervention.
 *
 * **When:** After successful soft-delete of document in database
 *
 * **Business Logic:** Document deletions are audited for compliance.
 * The original filename is preserved in the event for audit purposes even though
 * the document is deleted. Critical documents may require additional approval.
 */
export class InterventionDocumentDeletedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.documentId = params.documentId;
        this.documentType = params.documentType;
        this.originalFilename = params.originalFilename;
    }
}
/**
 * Emitted when a note is added to an intervention.
 *
 * **When:** After successful note creation in the database
 *
 * **Business Logic:** Notes are used for communication between technicians and
 * backoffice. Supports both user and technician authors for audit trail.
 */
export class InterventionNoteCreatedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.noteId = params.noteId;
        this.contentPreview = params.contentPreview;
    }
}
/**
 * Emitted when intervention dates are modified.
 *
 * **When:** After successful update of requestedDate, scheduledDate, or completedDate
 *
 * **Business Logic:** Date changes are critical for scheduling and SLA tracking.
 * All three dates are included (old and new values) to provide complete context.
 * Null values indicate the date was not set or was cleared.
 * Scheduled date changes may require technician notification.
 */
export class InterventionDateChangedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.oldStatus = params.oldStatus;
        this.newStatus = params.newStatus;
        this.oldRequestedDate = params.oldRequestedDate;
        this.newRequestedDate = params.newRequestedDate;
        this.oldScheduledDate = params.oldScheduledDate;
        this.newScheduledDate = params.newScheduledDate;
        this.oldCompletedDate = params.oldCompletedDate;
        this.newCompletedDate = params.newCompletedDate;
        this.timezone = params.timezone ?? null;
        this.oldRequestedAt = params.oldRequestedAt ?? null;
        this.newRequestedAt = params.newRequestedAt ?? null;
        this.oldScheduledAt = params.oldScheduledAt ?? null;
        this.newScheduledAt = params.newScheduledAt ?? null;
        this.oldCompletedAt = params.oldCompletedAt ?? null;
        this.newCompletedAt = params.newCompletedAt ?? null;
    }
}
/**
 * Who put the technician on the intervention. Drives the confirmation policy
 * (#1685): a self-accept sends no CONFIRMED message, an assignment made FOR the
 * technician does.
 */
export var AssignmentSource;
(function (AssignmentSource) {
    AssignmentSource["TECHNICIAN_ACCEPT"] = "technician_accept";
    AssignmentSource["MANUAL_ASSIGNMENT"] = "manual_assignment";
    AssignmentSource["QUOTE_VALIDATION"] = "quote_validation";
})(AssignmentSource || (AssignmentSource = {}));
export class InterventionTechnicianAssignedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.technicianId = params.technicianId;
        this.assignedBy = params.assignedBy;
        this.scheduledDate = params.scheduledDate;
        this.scheduledAt = params.scheduledAt ?? null;
        this.source = params.source;
        this.automated = params.automated ?? false;
    }
}
export class InterventionTechnicianRejectedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.technicianId = params.technicianId;
        this.reason = params.reason;
    }
}
export class InterventionAssignmentCanceledEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.previousTechnicianId = params.previousTechnicianId;
        this.reason = params.reason;
    }
}
export class InterventionDisputeReportedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.category = params.category;
        this.reason = params.reason;
    }
}
export class InterventionDisputeResolvedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
    }
}
/**
 * **When:** After an intervention is put on hold
 */
export class InterventionPutOnHoldEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.previousStatus = params.previousStatus;
        this.reason = params.reason;
        this.note = params.note;
    }
}
/**
 * **When:** After an intervention is resumed from hold
 */
export class InterventionResumedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.previousStatus = params.previousStatus;
        this.targetStatus = params.targetStatus;
        this.holdReason = params.holdReason;
    }
}
/**
 * **When:** After a cancelled intervention is reactivated
 */
export class InterventionReactivatedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.previousStatus = params.previousStatus;
    }
}
export class InterventionCompletedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.newStatus = params.newStatus;
        this.quoteRequested = params.quoteRequested;
    }
}
export class InterventionCancelledEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.previousStatus = params.previousStatus;
        this.reason = params.reason;
    }
}
export class InterventionClosedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
    }
}
/**
 * Emitted when a technician records their arrival time on site.
 *
 * **When:** After successful arrival time update in the database
 *
 * **Business Logic:** Arrival recording is critical for SLA tracking and
 * provides visibility into technician progress. The arrivalTime is the
 * actual time the technician arrived on site.
 */
export class JobReportArrivalRecordedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.arrivalTime = params.arrivalTime;
    }
}
/**
 * Emitted when a job report is created for an intervention.
 *
 * **When:** After successful job report creation in the database
 *
 * **Business Logic:** Job report creation indicates the technician has
 * started documenting their work. The report is in DRAFT status at this point.
 */
export class JobReportCreatedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.reportId = params.reportId;
        this.reportNumber = params.reportNumber;
    }
}
/**
 * Emitted when a job report is signed and PDF is generated.
 *
 * **When:** After successful signature capture and PDF generation
 *
 * **Business Logic:** Report signing completes the job report workflow.
 * The needsSecondVisit flag determines if the intervention is completed
 * or needs to be rescheduled for another visit.
 */
export class JobReportSignedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.reportId = params.reportId;
        this.reportNumber = params.reportNumber;
        this.needsSecondVisit = params.needsSecondVisit;
        this.isWorkCompleted = params.isWorkCompleted;
        this.resultingStatus = params.resultingStatus;
    }
}
/**
 * Emitted when a SUPER_ADMIN validates a signed job report, making it visible to
 * the report's organization (#1613).
 *
 * **When:** After the validation transaction commits, and only for the FIRST
 * validation — `validateReportForWeb` is idempotent, so a retry or a double-click
 * emits nothing and no consumer can deliver twice.
 *
 * **Business Logic:** This is the client-facing occurrence of the report
 * lifecycle: the signature (`JOB_REPORT_EVENTS.SIGNED`) is a platform review
 * signal, validation is what makes the report readable org-side. Consumers
 * (#1674 email, #1583 organization channel) resolve recipients and the
 * intervention reference by re-reading the intervention, the same way
 * `JobReportSubmittedResolver` does — an access decision must be taken against
 * the row as it stands when the delivery is materialized, not against a snapshot.
 * Report content and the PDF deliberately never ride on the event.
 */
export class JobReportValidatedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.reportId = params.reportId;
        this.reportNumber = params.reportNumber;
    }
}
/**
 * Emitted when a SUPER_ADMIN forces an intervention status change outside normal lifecycle rules.
 *
 * **When:** After successful forced status update by SUPER_ADMIN
 *
 * **Business Logic:** This is an administrative override that bypasses normal lifecycle
 * validation. Used for edge cases and emergency situations. The reason field is mandatory
 * and logged for audit purposes. This event should trigger special activity log entries.
 */
export class InterventionStatusForcedEvent {
    constructor(params) {
        this.interventionId = params.interventionId;
        this.organizationId = params.organizationId;
        this.actorId = params.actorId;
        this.actorTechnicianId = params.actorTechnicianId;
        this.timestamp = params.timestamp;
        this.title = params.title;
        this.oldStatus = params.oldStatus;
        this.newStatus = params.newStatus;
        this.reason = params.reason;
    }
}
